import CryptoJS from 'crypto-js'

// Get the secret key from environment variables
const SECRET_KEY = import.meta.env.VITE_SECRET_KEY || 'fallback-dev-key-change-in-production'

/**
 * Encrypt data using AES encryption
 * @param data - The data to encrypt (will be JSON stringified)
 * @returns Encrypted string
 */
export function encrypt<T>(data: T): string {
  try {
    const jsonString = JSON.stringify(data)
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString()
    return encrypted
  } catch (error) {
    console.error('Encryption failed:', error)
    throw new Error('Failed to encrypt data')
  }
}

/**
 * Decrypt data using AES decryption
 * @param encryptedData - The encrypted string to decrypt
 * @returns Decrypted and parsed data
 */
export function decrypt<T>(encryptedData: string): T | null {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY)
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8)
    
    if (!decryptedString) {
      console.error('Failed to decrypt data - invalid key or corrupted data')
      return null
    }
    
    return JSON.parse(decryptedString) as T
  } catch (error) {
    console.error('Decryption failed:', error)
    return null
  }
}

/**
 * Securely store encrypted data in localStorage
 * @param key - Storage key
 * @param data - Data to encrypt and store
 */
export function setEncryptedLocalStorage<T>(key: string, data: T): void {
  try {
    const encrypted = encrypt(data)
    localStorage.setItem(key, encrypted)
  } catch (error) {
    console.error('Failed to store encrypted data:', error)
  }
}

/**
 * Retrieve and decrypt data from localStorage
 * @param key - Storage key
 * @returns Decrypted data or null if not found/invalid
 */
export function getEncryptedLocalStorage<T>(key: string): T | null {
  try {
    const encrypted = localStorage.getItem(key)
    if (!encrypted) return null
    
    return decrypt<T>(encrypted)
  } catch (error) {
    console.error('Failed to retrieve encrypted data:', error)
    return null
  }
}

/**
 * Securely store encrypted data in sessionStorage
 * @param key - Storage key
 * @param data - Data to encrypt and store
 */
export function setEncryptedSessionStorage<T>(key: string, data: T): void {
  try {
    const encrypted = encrypt(data)
    sessionStorage.setItem(key, encrypted)
  } catch (error) {
    console.error('Failed to store encrypted session data:', error)
  }
}

/**
 * Retrieve and decrypt data from sessionStorage
 * @param key - Storage key
 * @returns Decrypted data or null if not found/invalid
 */
export function getEncryptedSessionStorage<T>(key: string): T | null {
  try {
    const encrypted = sessionStorage.getItem(key)
    if (!encrypted) return null
    
    return decrypt<T>(encrypted)
  } catch (error) {
    console.error('Failed to retrieve encrypted session data:', error)
    return null
  }
}

/**
 * Remove encrypted data from localStorage
 * @param key - Storage key
 */
export function removeEncryptedLocalStorage(key: string): void {
  localStorage.removeItem(key)
}

/**
 * Remove encrypted data from sessionStorage
 * @param key - Storage key
 */
export function removeEncryptedSessionStorage(key: string): void {
  sessionStorage.removeItem(key)
} 